import { type MutableRefObject, useLayoutEffect, useMemo, useRef } from 'react';
import { Plus, Search } from 'lucide-react';
import type { FidsTrackingRouteSummary } from 'contracts/FidsTrackingRoutes';
import * as S from './styles';

export const STATUS_LABELS = { draft: 'Rascunho', active: 'Ativo' } as const;

const plural = (count: number, singular: string, pluralForm: string) =>
  `${count} ${count === 1 ? singular : pluralForm}`;

// Mesma busca das outras telas: sem diferença de acentos e maiúsculas.
export const normalize = (text: string) =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

interface Props {
  routes: FidsTrackingRouteSummary[];
  selectedId: number | null;
  // A busca e a rolagem ficam na página: a lista é montada de novo a cada rastreio aberto.
  search: string;
  onSearchChange: (search: string) => void;
  scrollTopRef: MutableRefObject<number>;
  isLoading: boolean;
  isCreating: boolean;
  loadError: string | null;
  onRetry: () => void;
  onSelect: (id: number) => void;
  onCreate: () => void;
}

export const TrackingRouteList: React.FC<Props> = ({
  routes,
  selectedId,
  search,
  onSearchChange,
  scrollTopRef,
  isLoading,
  isCreating,
  loadError,
  onRetry,
  onSelect,
  onCreate,
}) => {
  const listRef = useRef<HTMLOListElement>(null);
  const visibleRoutes = useMemo(
    () =>
      routes.filter(({ name }) =>
        normalize(name).includes(normalize(search.trim()))
      ),
    [routes, search]
  );

  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = scrollTopRef.current;
  }, [scrollTopRef, visibleRoutes.length]);

  return (
    <S.Panel aria-label="Rastreios">
      <S.PanelHeader>
        <div>
          <h2 className="font-bold text-slate-800">Rastreios</h2>
          <p className="mt-1 text-xs text-slate-500">
            {plural(routes.length, 'rastreio', 'rastreios')}
          </p>
        </div>
        <S.PrimaryButton
          type="button"
          disabled={isLoading || isCreating}
          onClick={onCreate}
        >
          <Plus size={16} aria-hidden="true" />
          {isCreating ? 'Criando...' : 'Novo rastreio'}
        </S.PrimaryButton>
      </S.PanelHeader>
      <div className="p-3">
        <label className="relative block">
          <span className="sr-only">Buscar rastreios</span>
          <Search
            size={15}
            aria-hidden="true"
            className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <S.SearchInput
            type="search"
            placeholder="Buscar rastreios..."
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            $invalid={false}
          />
        </label>
      </div>
      {loadError ? (
        <div
          role="alert"
          className="flex flex-col items-start gap-2 px-4 pb-4 text-sm text-red-700"
        >
          <p>{loadError}</p>
          <button
            type="button"
            className="font-bold underline"
            onClick={onRetry}
          >
            Tentar novamente
          </button>
        </div>
      ) : isLoading ? (
        <p className="p-6 text-center text-sm text-slate-500">
          Carregando rastreios...
        </p>
      ) : routes.length === 0 ? (
        <p className="p-6 text-center text-sm text-slate-500">
          Nenhum rastreio. Crie o primeiro em "Novo rastreio".
        </p>
      ) : visibleRoutes.length === 0 ? (
        <p className="p-6 text-center text-sm text-slate-500">
          Nenhum rastreio encontrado.
        </p>
      ) : (
        <S.RouteList
          ref={listRef}
          aria-label="Lista de rastreios"
          onScroll={(event) => {
            scrollTopRef.current = event.currentTarget.scrollTop;
          }}
        >
          {visibleRoutes.map((route) => (
            <S.RouteItem key={route.id} $active={route.id === selectedId}>
              <button
                type="button"
                aria-current={route.id === selectedId ? 'true' : undefined}
                onClick={() => onSelect(route.id)}
                className="min-w-0 flex-1 text-left"
              >
                <span className="block truncate text-sm font-bold text-slate-800">
                  {route.name}
                </span>
                <span className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-slate-600">
                  <S.StatusBadge $status={route.status}>
                    {STATUS_LABELS[route.status]}
                  </S.StatusBadge>
                  {`${route.hasCenter ? 'Centro marcado' : 'Sem centro'} · ${plural(
                    route.warehouseCount,
                    'armazém',
                    'armazéns'
                  )}`}
                </span>
              </button>
            </S.RouteItem>
          ))}
        </S.RouteList>
      )}
    </S.Panel>
  );
};
